import { PlannedMeal } from '../redux/mealPlanSlice';
import { InventoryItem } from '../redux/inventorySlice';
import { RecipeIngredient } from '../types/recipe';
import { GroceryCategory, GroceryItem, WasteReductionMetrics, SkippedIngredient, CATEGORY_METADATA } from '../types/groceryList';
import { calculateLiveMatchScore } from './mealPlanningService';
import { isCountUnit } from '../utils/inventoryUtils';
import { convertBetweenUnits, areUnitsCompatible } from '../utils/unitConversion';
import { getCanonicalIngredient } from '../utils/ingredientSynonyms';
import { isFuzzyMatch, similarityScore } from '../utils/stringMatching';
import { parseAmount, parseUnit as parseUnitFromText, formatQuantity } from '../utils/ingredientParser';
import { stapleKeyForLine, stapleKeysForLine, isTapWater } from '../constants/staples';
import { cleanPantryName } from '../utils/pantryFoodName';
import { isYieldLine } from '../utils/yieldLine';
import { buildPantryIndex, coverageForLine } from './ingredientCoverageService';
import { isCookedLeftover } from '../utils/pantryLocation';
import { categoryFromVocabulary } from './groceryAisle';
// Note: ingredientConsolidation utils no longer used - consolidation now preserves extracted names directly

/**
 * Descriptors that MUST be preserved (they distinguish different ingredients)
 * These descriptors change the fundamental nature of the ingredient
 */
const PRESERVE_DESCRIPTORS = [
  // Freshness/Processing (different shelf life, usage, taste)
  'fresh', 'dried', 'canned', 'frozen', 'raw', 'cooked',
  'frais', 'fraîche', 'séché', 'séchée', 'congelé', 'congelée',

  // Form/Texture (affects cooking method and result)
  'whole', 'ground', 'crushed', 'pureed', 'paste',
  'entier', 'entière', 'moulu', 'moulue', 'écrasé', 'écrasée',

  // Variety-specific (different taste/texture)
  'sweet', 'hot', 'spicy', 'mild',
  'cherry', 'roma', 'beefsteak', 'grape', // tomato varieties
  'russet', 'yukon gold', 'sweet', // potato varieties
];

/**
 * Colour words, French to English.
 *
 * Colour is normally a descriptor and gets stripped ("black pepper" and "pepper"
 * are the same shopping decision). For a short list of produce it is IDENTITY:
 * red cabbage is not green cabbage, and a pantry holding one does not cover a
 * recipe calling for the other. COLOUR_SIGNIFICANT_NOUNS is that list.
 */
const COLOUR_WORDS: Record<string, string> = {
  red: 'red', green: 'green', yellow: 'yellow', white: 'white',
  black: 'black', purple: 'purple', orange: 'orange', pink: 'pink',
  rouge: 'red', rouges: 'red',
  vert: 'green', verte: 'green', verts: 'green', vertes: 'green',
  jaune: 'yellow', jaunes: 'yellow',
  blanc: 'white', blancs: 'white', blanche: 'white', blanches: 'white',
  noir: 'black', noirs: 'black', noire: 'black', noires: 'black',
  violet: 'purple', violette: 'purple',
};

/**
 * Produce where the colour changes what you have to buy. Keep this list tight —
 * every entry here makes two previously-equal ingredients unequal, which means
 * an extra line on the grocery list when the user really did have a substitute.
 * English, applied AFTER the FR→EN translation so `chou` has become `cabbage`.
 */
const COLOUR_SIGNIFICANT_NOUNS = new Set([
  'cabbage', 'onion', 'grape', 'bean', 'lentil',
  // Wine: red and white are not substitutes for each other in a recipe or on a
  // shopping list, and without this the pantry's duplicate matcher offers to
  // merge "vin blanc" into "vin rouge" — which deletes a bottle.
  'wine',
]);
// Deliberately NOT here: `pepper`. Colour genuinely distinguishes poivron rouge
// from poivron vert, but it also splits "black pepper" away from "pepper", and
// stapleKeyOfNormalized('black pepper') is null while the bare 'pepper' is a
// staple — so black pepper would stop counting as a staple and start showing up
// on grocery lists. Not worth it for a cabbage fix; revisit with a staples change.

/**
 * Category keywords for intelligent categorization
 */
const CATEGORY_KEYWORDS: Record<GroceryCategory, string[]> = {
  [GroceryCategory.Produce]: [
    'lettuce', 'tomato', 'onion', 'carrot', 'potato', 'apple', 'banana', 'orange',
    'spinach', 'kale', 'broccoli', 'cauliflower', 'pepper', 'celery', 'cucumber',
    'avocado', 'lemon', 'lime', 'garlic', 'ginger', 'mushroom', 'zucchini',
    'eggplant', 'squash', 'cabbage', 'green bean', 'peas', 'corn', 'asparagus',
  ],
  [GroceryCategory.Dairy]: [
    'milk', 'cheese', 'yogurt', 'butter', 'cream', 'sour cream', 'cottage cheese',
    'cheddar', 'mozzarella', 'parmesan', 'feta', 'goat cheese', 'ricotta',
    'whipped cream', 'half and half', 'buttermilk',
  ],
  [GroceryCategory.Meat]: [
    'chicken', 'beef', 'pork', 'turkey', 'lamb', 'fish', 'salmon', 'tuna',
    'shrimp', 'bacon', 'sausage', 'ham', 'steak', 'ground beef', 'ground turkey',
    'tilapia', 'cod', 'halibut', 'mussels', 'crab', 'lobster',
  ],
  [GroceryCategory.Bakery]: [
    'bread', 'baguette', 'roll', 'bagel', 'croissant', 'muffin', 'tortilla',
    'pita', 'naan', 'bun', 'biscuit', 'pastry', 'donut', 'cake',
  ],
  [GroceryCategory.Pantry]: [
    'rice', 'pasta', 'orzo', 'flour', 'sugar', 'oil', 'olive oil',
    'vinegar', 'soy sauce', 'honey', 'maple syrup',
    'beans', 'lentils', 'chickpeas', 'tomato sauce', 'tomato paste', 'broth',
    'stock', 'canned tomato', 'coconut milk', 'peanut butter', 'jam', 'oats',
  ],
  [GroceryCategory.Spices]: [
    'salt', 'pepper', 'black pepper', 'white pepper', 'red pepper', 'cayenne',
    'paprika', 'cumin', 'oregano', 'basil', 'thyme', 'rosemary', 'bay leaf',
    'cinnamon', 'nutmeg', 'clove', 'cardamom', 'coriander', 'turmeric', 'ginger',
    'garlic powder', 'onion powder', 'chili powder', 'curry', 'sage', 'parsley',
    'dill', 'mint', 'cilantro', 'tarragon', 'fennel', 'allspice', 'anise',
    'mustard', 'saffron', 'vanilla', 'extract', 'seasoning', 'spice',
  ],
  [GroceryCategory.Frozen]: [
    'frozen', 'ice cream', 'frozen pizza', 'frozen vegetables', 'frozen fruit',
    'popsicle', 'frozen dinner',
  ],
  [GroceryCategory.Beverages]: [
    'juice', 'soda', 'coffee', 'tea', 'water', 'wine', 'beer', 'liquor',
    'sparkling water', 'coconut water', 'almond milk', 'soy milk', 'oat milk',
  ],
  [GroceryCategory.Other]: [],
};

// Compiled once, on first use (#668). The normalizer used to build ~400
// RegExp objects per call, and it runs once per active pantry item per meal
// card, so a large pantry froze the planner for seconds. The tables themselves
// stay inside the function so the sequence of replacements is unchanged.
let MULTI_WORD_PHRASE_REGEXES: RegExp[] | null = null;
let DESCRIPTOR_REGEXES: RegExp[] | null = null;
let PRESERVED_DESCRIPTOR_REGEXES: Array<[RegExp, string]> | null = null;

// The function is pure, and pantry names repeat across every screen that
// matches them, so remember results. Bounded: cleared wholesale when full.
const NORMALIZE_CACHE = new Map<string, string>();
const NORMALIZE_CACHE_MAX = 5000;

/**
 * Normalize ingredient name for matching and deduplication
 * Enhanced with fuzzy matching for common variations and French→English translation
 */
export function normalizeIngredientName(name: string): string {
  const hit = NORMALIZE_CACHE.get(name);
  if (hit !== undefined) return hit;
  const out = normalizeIngredientNameUncached(name);
  if (NORMALIZE_CACHE.size >= NORMALIZE_CACHE_MAX) NORMALIZE_CACHE.clear();
  NORMALIZE_CACHE.set(name, out);
  return out;
}

function normalizeIngredientNameUncached(name: string): string {
  let normalized = name.toLowerCase().trim();

  // STEP 1: Remove quantity + unit prefix at the beginning
  // Matches patterns like: "45 ml", "2 cups", "1/2 tsp", "3.5 ounces", "160 g"
  // IMPORTANT: Space must exist between number and single-letter units to avoid "1 large" → "1 l"
  const quantityUnitPattern = /^[\d\/\.\s]+\s+(g|l|ml|liter|liters|milliliter|milliliters|cup|cups|tablespoon|tablespoons|tbsp|teaspoon|teaspoons|tsp|ounce|ounces|oz|pound|pounds|lb|lbs|gram|grams|kg|kilogram|kilograms|c\.|c\s|à\s+soupe|à\s+café)(?:\s+|$)/i;
  normalized = normalized.replace(quantityUnitPattern, '');

  // STEP 2: Remove parenthetical content (like "(3 c. à soupe)")
  normalized = normalized.replace(/\([^)]*\)/g, '');

  // STEP 3: Remove multi-word French cooking phrases (before single words)
  const multiWordPhrases = [
    'en dés', 'en morceaux', 'en tranches', 'en rondelles', 'en cubes', 'en julienne',
    'en lanières', 'en lamelles', 'en quartiers', 'en deux',
    'coupé en', 'coupés en', 'coupée en', 'coupées en',
    'épépiné et', 'épépinés et', 'épépinée et', 'épépinées et',
  ];
  if (!MULTI_WORD_PHRASE_REGEXES) {
    // Use a more flexible pattern that handles accents
    MULTI_WORD_PHRASE_REGEXES = multiWordPhrases.map(
      phrase => new RegExp(phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')
    );
  }
  for (const re of MULTI_WORD_PHRASE_REGEXES) {
    normalized = normalized.replace(re, '');
  }

  // STEP 4: Remove any remaining numbers
  normalized = normalized.replace(/\d+/g, '');

  // STEP 5: Remove punctuation and normalize whitespace
  //
  // "+" is stripped alongside . and , because compound recipe lines carry it
  // ("125 ml + 30 ml (2 c. à soupe) d'huile d'olive"). Left in, it glues to
  // whichever token follows and the same oil reads as "+ huile olive" from the
  // recipe but "+ ml huile olive" from the pantry, which then fails even
  // containment because the stray unit sits between the two.
  normalized = normalized
    .replace(/[.,+]/g, '') // Remove punctuation
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();

  // STEP 5.1: Drop measurement units left stranded mid-string. STEP 1 only
  // strips a leading "<qty> <unit>"; a compound line has a second quantity
  // inside it, and once the digits go in STEP 4 the bare unit survives. That
  // lone "ml" is the whole reason a pantry entry and its own recipe line stop
  // matching, so it has to go before any comparison.
  const STRANDED_UNITS = new Set([
    'ml', 'l', 'g', 'kg', 'mg', 'oz', 'lb', 'lbs',
    'cup', 'cups', 'tasse', 'tasses', 'tbsp', 'tsp',
    'c', 'cuillere', 'cuillère', 'cuilleres', 'cuillères',
  ]);
  normalized = normalized
    .split(' ')
    .filter(tok => tok && !STRANDED_UNITS.has(tok))
    .join(' ')
    .trim();

  // STEP 5.5: Remove French apostrophe contractions (d', l', etc.)
  // Must happen before descriptor removal since apostrophe breaks word boundaries
  // Handle both straight apostrophe (') and typographic/curly apostrophe (')
  normalized = normalized.replace(/\b[dljcmnts]['\u2019]/gi, ''); // d', l', j', c', m', n', t', s'

  // STEP 6: Remove single-word descriptors and qualifiers (UPDATED with preservation)
  // Using more flexible pattern for accented characters
  const allDescriptorsToRemove = [
    // English descriptors
    'fresh', 'freshly', 'frozen', 'canned', 'dried', 'organic', 'raw', 'cooked',
    'whole', 'sliced', 'diced', 'chopped', 'minced', 'crushed', 'grated', 'ground',
    'finely', 'coarsely', 'roughly', 'thinly', 'thickly', 'lightly', 'extra-virgin', 'virgin',
    'large', 'medium', 'small', 'baby', 'mini', 'petit', 'petite', 'petits', 'petites',
    'juice', 'zest', 'puree', 'powder', 'paste',
    'store-bought', 'preserved', 'kosher', 'sea', 'table', 'iodized',
    'pitted', 'peeled', 'torn', 'left', 'half', 'halves', 'seedless', 'boneless', 'skinless',
    'for', 'garnish', 'serving', 'optional', 'such', 'as', 'see', 'tip', 'note',
    'in', 'advance', 'ahead', 'and', 'or', 'of', 'with', 'from',
    'facultatif', 'facultative', // French: optional
    'persian', 'cherry', 'roma', 'beefsteak', 'grape', 'libanais', 'libanaises', // Specific varieties
    'pearl', 'medjool', 'deglet', 'israeli', 'israélien', 'israélienne',
    'white', 'red', 'yellow', 'green', 'purple', 'black', 'orange', 'pink',
    'rouge', 'rouges', 'jaune', 'jaunes', 'vert', 'verts', 'verte', 'vertes',
    'blanc', 'blancs', 'blanche', 'blanches', 'noir', 'noirs', 'noire', 'noires',
    'cloves', 'clove', 'pieces', 'piece', 'bunch', 'head', 'stalk',
    // French articles and prepositions (d' handled separately due to apostrophe)
    'de', 'du', 'des', 'la', 'le', 'les', 'et', 'ou', 'à', 'au', 'aux',
    // French ingredient forms
    'jus', 'zeste', 'purée', 'poudre', // juice, zest, puree, powder
    // French cooking preparation terms
    'ciselée', 'ciselé', 'ciselées', 'ciselés', // chopped/minced
    'émincé', 'émincée', 'émincés', 'émincées', // sliced thin
    'haché', 'hachée', 'hachés', 'hachées', // chopped/ground
    'râpé', 'râpée', 'râpés', 'râpées', // grated
    'coupé', 'coupée', 'coupés', 'coupées', // cut
    'tranché', 'tranchée', 'tranchés', 'tranchées', // sliced
    'écrasé', 'écrasée', 'écrasés', 'écrasées', // crushed
    'moulu', 'moulue', 'moulus', 'moulues', // ground
    'entier', 'entière', 'entiers', 'entières', // whole
    'frais', 'fraîche', 'fraîches', // fresh
    'congelé', 'congelée', 'congelés', 'congelées', // frozen
    'séché', 'séchée', 'séchés', 'séchées', // dried
    'épépiné', 'épépinée', 'épépinés', 'épépinées', // seeded
    'grillé', 'grillée', 'grillés', 'grillées', // grilled/toasted
    'rôti', 'rôtie', 'rôtis', 'rôties', // roasted
    'salé', 'salée', 'salés', 'salées', // salted
    // French adverbs
    'finement', 'grossièrement', 'légèrement', 'uniformément',
    // French ingredient parts
    'noix', 'pin', // walnut/nut, pine (as in pine nuts)
  ];

  // NEW: Filter out preserved descriptors (they distinguish ingredients)
  // Colours are held back here and decided in STEP 8.5, once the noun has been
  // translated and we can tell "red cabbage" (identity) from "black pepper"
  // (descriptor). Stripping them here would lose the information for good.
  if (!DESCRIPTOR_REGEXES) {
    const descriptorsToRemove = allDescriptorsToRemove.filter(
      descriptor =>
        !PRESERVE_DESCRIPTORS.includes(descriptor.toLowerCase()) &&
        !COLOUR_WORDS[descriptor.toLowerCase()]
    );
    // More flexible pattern that works with accented characters
    // Match word at start, end, or surrounded by spaces
    DESCRIPTOR_REGEXES = descriptorsToRemove.map(
      descriptor =>
        new RegExp(`(^|\\s)${descriptor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s|$)`, 'gi')
    );
  }
  for (const re of DESCRIPTOR_REGEXES) {
    normalized = normalized.replace(re, ' ');
  }

  // STEP 6.5: Normalize preserved French descriptors to English
  const preservedDescriptorMap: Record<string, string> = {
    'frais': 'fresh',
    'fraîche': 'fresh',
    'séché': 'dried',
    'séchée': 'dried',
    'congelé': 'frozen',
    'congelée': 'frozen',
    'entier': 'whole',
    'entière': 'whole',
    'moulu': 'ground',
    'moulue': 'ground',
    'écrasé': 'crushed',
    'écrasée': 'crushed',
  };

  // Replace French preserved descriptors with English equivalents
  if (!PRESERVED_DESCRIPTOR_REGEXES) {
    PRESERVED_DESCRIPTOR_REGEXES = Object.entries(preservedDescriptorMap).map(
      ([french, english]): [RegExp, string] => [new RegExp(`\\b${french}\\b`, 'gi'), english]
    );
  }
  for (const [re, english] of PRESERVED_DESCRIPTOR_REGEXES) {
    normalized = normalized.replace(re, english);
  }

  // STEP 7: Clean up extra spaces again
  normalized = normalized.replace(/\s+/g, ' ').trim();

  // STEP 8: French to English translation (for common produce/ingredients)
  const frenchToEnglish: Record<string, string> = {
    // Vegetables
    'concombre': 'cucumber',
    'concombres': 'cucumber',
    'oignon': 'onion',
    'oignons': 'onion',
    'carotte': 'carrot',
    'carottes': 'carrot',
    'poivron': 'pepper',
    'poivrons': 'pepper',
    'tomate': 'tomato',
    'tomates': 'tomato',
    'pomme de terre': 'potato',
    'pommes de terre': 'potato',
    // Post-descriptor forms: STEP 6 strips the article "de", so by the time the
    // lookup runs the string is "pomme terre" and the entries above never hit.
    'pomme terre': 'potato',
    'pommes terre': 'potato',
    'ail': 'garlic',
    // Wine. Needed for the colour rule below to bite: COLOUR_SIGNIFICANT_NOUNS
    // is applied to the English noun, so an untranslated "vin" never reached it
    // and "vin rouge" and "vin blanc" both flattened to "vin".
    'vin': 'wine',
    'vins': 'wine',
    'laitue': 'lettuce',
    'épinard': 'spinach',
    'épinards': 'spinach',
    'brocoli': 'broccoli',
    'chou-fleur': 'cauliflower',
    // The cabbage family. `chou` was missing entirely, so a French recipe line
    // stayed `chou` while an English pantry item became `cabbage` and the two
    // never matched — the bug this table gap caused.
    'chou': 'cabbage',
    'choux': 'cabbage',
    'chou frisé': 'kale',
    'chou de bruxelles': 'brussels sprout',
    'choux de bruxelles': 'brussels sprout',
    // Same "de" stripping as pomme de terre — these are the forms that survive.
    'chou bruxelles': 'brussels sprout',
    'choux bruxelles': 'brussels sprout',
    'chou nappa': 'napa cabbage',
    'chou chinois': 'napa cabbage',
    'champignon': 'mushroom',
    'champignons': 'mushroom',
    'courgette': 'zucchini',
    'courgettes': 'zucchini',
    'aubergine': 'eggplant',
    'aubergines': 'eggplant',
    // Herbs
    'menthe': 'mint',
    'persil': 'parsley',
    'coriandre': 'cilantro',
    'basilic': 'basil',
    'thym': 'thyme',
    'romarin': 'rosemary',
    // Fruits
    'pomme': 'apple',
    'pommes': 'apple',
    'citron': 'lemon',
    'citrons': 'lemon',
    'citron vert': 'lime',
    'citrons verts': 'lime',
    'avocat': 'avocado',
    'avocats': 'avocado',
    // Pantry
    'huile': 'oil',
    "huile d'olive": 'olive oil',
    'beurre': 'butter',
    'farine': 'flour',
    'sucre': 'sugar',
  };

  // STEP 8a: whole-string match first. This has to run BEFORE the colour is
  // lifted, because for some names the colour is part of the identity rather
  // than a variant of it — "citron vert" is a lime, not a green lemon.
  let liftedColour = '';
  if (frenchToEnglish[normalized]) {
    normalized = frenchToEnglish[normalized];
  } else {
    // STEP 8b: no whole-string hit. Lift the colour out and translate the bare
    // noun. French puts the colour after the noun ("chou rouge"), English before
    // it ("red cabbage"), and the table is keyed on the noun — so one entry
    // covers every colour variant instead of enumerating chou rouge / vert /
    // blanc by hand. The colour comes back (or is dropped) in STEP 8.5.
    const tokens = normalized.split(' ').filter(Boolean);
    const colourIdx = tokens.findIndex(tok => COLOUR_WORDS[tok]);
    if (colourIdx !== -1 && tokens.length > 1) {
      liftedColour = COLOUR_WORDS[tokens[colourIdx]];
      tokens.splice(colourIdx, 1);
      normalized = tokens.join(' ').trim();
    }

    if (frenchToEnglish[normalized]) {
      normalized = frenchToEnglish[normalized];
    }
    // No head-noun fallback on purpose. Translating just the first word of an
    // unrecognised phrase produces franglais — "pomme de terre" loses "de" to
    // the descriptor pass, and translating the head turned "pomme terre" into
    // "apple terre", which matches nothing and reads like a bug. Unrecognised
    // French stays whole and untranslated; add a table entry instead.
  }

  // STEP 9: Handle pluralization: remove trailing 's' for simple plurals
  // (Simple heuristic: works for most common ingredients)
  if (normalized.endsWith('s') && normalized.length > 3) {
    // Check if it's not a word that naturally ends in 's' (like 'beans', 'peas', 'lentils', 'couscous')
    const naturalSEndings = ['beans', 'peas', 'lentils', 'oats', 'grits', 'grains', 'couscous', 'hummus', 'asparagus'];
    if (!naturalSEndings.some(ending => normalized === ending)) {
      normalized = normalized.slice(0, -1);
    }
  }

  // STEP 10: Apply synonym normalization (NEW!)
  // Converts synonyms to canonical form (e.g., "scallion" → "green onion")
  normalized = getCanonicalIngredient(normalized);

  // STEP 8.5 (applied last, once the noun is fully canonical): put the colour
  // back only where it changes what you have to buy. Red cabbage does not cover
  // a recipe calling for green cabbage, so those must stay different strings.
  // Everywhere else the colour is a descriptor and stays dropped, which is the
  // behaviour every other ingredient had before.
  if (liftedColour && COLOUR_SIGNIFICANT_NOUNS.has(normalized)) {
    normalized = `${liftedColour} ${normalized}`;
  }

  return normalized;
}

/**
 * Measurement units that mark a line as an ingredient rather than a title.
 *
 * French units are sampled from the 3,564 French ingredient lines in the shipped
 * catalog (public/assets/recipes/details), most frequent first: ml, g, c.,
 * tasse(s), gousse(s), boîte, tranches, feuilles, branche(s), cuillère(s), pincée.
 */
// "c\." used to sit inside the \b(...)\b group, where it could never match:
// a word boundary cannot exist between "." and the space that follows it, so
// the single most common French unit in the catalog was invisible to this
// test. Every French line of 7+ words that led with a digit and had no comma
// was therefore dropped as junk — "1 c. à soupe de vinaigre balsamique"
// vanished from the grocery list before anything else got to look at it
// (#637). The two "c." forms now live outside the group with their own
// anchors; the group itself is unchanged.
/**
 * Any unit that marks a line as a measured ingredient. Length units are in here
 * because French recipes size the food itself — "8 côtelettes d'agneau d'environ
 * 2 cm d'épaisseur" is 7 words and was being discarded as a recipe title, so the
 * lamb never reached the grocery list (found while fixing #594).
 */
const MEASUREMENT_UNIT =
  /(?:\b(?:g|kg|mg|ml|cl|dl|l|oz|lb|lbs|cup|cups|tbsp|tsp|tablespoons?|teaspoons?|ounces?|grams?|pounds?|pints?|quarts?|gallons?|cans?|cloves?|bunch(?:es)?|pieces?|slices?|sprigs?|stalks?|heads?|pinch(?:es)?|dash(?:es)?|handfuls?|packages?|packets?|jars?|sticks?|ears?|tasses?|cuilleres?|cuillères?|cuillerees?|cuillerées?|gousses?|boites?|boîtes?|conserves?|tranches?|feuilles?|branches?|brins?|morceaux?|morceau|pincees?|pincées?|sachets?|paquets?|litres?|millilitres?|douzaines?|bottes?|filets?|noix|poignees?|poignées?|pots?|onces?|croûtes?|croutes?|barquettes?|contenants?|bouquets?|cm|mm|po|pouces?|inch(?:es)?)\b|\bc\.(?=\s)|\bc\.?\s*à\s*(?:soupe|th[ée]|caf[ée])\b)/i;

/**
 * Heuristic filter to reject non-ingredient garbage that recipe scrapers
 * sometimes include in the ingredients[] array (titles, instructions, tips,
 * hashtags, nutrition info, marketing text, etc.).
 *
 * Returns true if the text looks like a real ingredient line.
 */
export function isLikelyIngredient(text: string): boolean {
  if (!text || !text.trim()) return false;

  const t = text.trim();

  // Too long — real ingredients rarely exceed 120 chars
  if (t.length > 120) return false;

  // Contains hashtags (#word)
  if (/#\w/.test(t)) return false;

  // Contains emojis (common Unicode emoji ranges)
  if (/[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u.test(t)) return false;

  // Contains URLs
  if (/https?:\/\/|www\./i.test(t)) return false;

  // Contains pipe character (nutrition tables)
  if (t.includes('|')) return false;

  // 2+ exclamation marks (marketing text)
  if ((t.match(/!/g) || []).length >= 2) return false;

  // Nutrition keywords — terms that only ever appear on a nutrition panel.
  if (/\b(calories|calorie|carbohydrates|sodium|cholesterol|saturated|trans fat|daily value|serving size|nutrition facts|nutritional info|per serving|valeur quotidienne|valeurs nutritives|par portion)\b/i.test(t)) return false;

  // Macro nutrients need context, because they double as ordinary ingredients:
  // "brown sugar", "bacon fat", "protein powder" are groceries, and blocking the
  // bare word silently deleted them from the list. Only reject when the line
  // reads like a panel — the nutrient followed by a mass ("Total fat 12g",
  // "protein: 25g"), which is never how an ingredient is written. A genuine
  // ingredient puts the quantity first ("500 g sugar"), so it survives.
  if (/\b(protein|fat|fibre|fiber|sugars?|carbs?)\b[\s:]*\d+\s*(g|mg|kcal|%)/i.test(t)) return false;
  if (/\btotal\s+(fat|carbs?|sugars?)\b/i.test(t)) return false;

  // Starts with instruction verbs
  if (/^\s*(marinate|reheat|serve|bake|cook|preheat|let|allow|place|remove|stir|mix|combine|whisk|fold|drain|rinse|set aside|bring|reduce|simmer|boil|sauté|fry|grill|roast|broil|garnish with|top with|drizzle|season to taste)\b/i.test(t)) return false;

  // Starts with meta patterns (English and French)
  if (/^\s*(step\s+\d|notes?:|tips?:|important|to reheat|to serve|to store|for the|for serving|for garnish|make ahead|prep time|cook time|total time|yield|serves?\s+\d)/i.test(t)) return false;
  if (/^\s*(étape\s+\d|etape\s+\d|notes?\s*:|astuces?\s*:|conseils?\s*:|pour servir|pour la garniture|préparation\s*:|temps de (cuisson|préparation)|donne\s+\d|portions?\s*:)/i.test(t)) return false;

  // The recipe's yield, which French sites put inside `recipeIngredient`
  // ("Pour 4 personnes"). The meta patterns above caught "Portions :" and
  // "Donne N" but not the two commonest forms, so the yield was priced up as a
  // grocery (#594). `isYieldLine` is anchored precisely so that "4 portions de
  // saumon" stays food.
  if (isYieldLine(t)) return false;

  // Marketing / social media keywords
  if (/\b(digital cookbook|subscribe|check out|follow me|follow us|click here|sign up|newsletter|download|instagram|facebook|tiktok|youtube|pinterest|blog|website)\b/i.test(t)) return false;

  // ALL-CAPS with 3+ words (likely a title/heading)
  const words = t.split(/\s+/);
  if (words.length >= 3 && t === t.toUpperCase() && /[A-Z]/.test(t)) return false;

  // Starts with a number followed by 6+ words (recipe title with leading number,
  // e.g. "5 Easy Weeknight Dinners You Will Love").
  //
  // A line is spared when it mentions a measurement unit ANYWHERE, not just
  // immediately after the quantity. Requiring adjacency dropped perfectly normal
  // ingredients whose unit trails the food word — "3 garlic cloves finely chopped
  // with a knife" in English, and nearly every French line once translated,
  // because the allowlist was almost entirely English.
  //
  // A line is also spared when it carries a parenthetical note. "4 pommes de
  // terre Russett (1 par personne)" is 8 words and was being dropped outright —
  // the user saw the potatoes on the recipe screen and never on the list (found
  // while fixing #594). Recipe titles do not carry parentheticals; ingredient
  // lines carry them constantly, for exactly the same reason they carry commas.
  //
  // A line is also spared when it contains a comma. Ingredient lines overwhelm-
  // ingly read "<qty> <food>, <preparation>" ("2 ripe peaches, pitted and
  // diced"), while recipe titles rarely use one. This matters most for French,
  // which runs about 20% wordier than English for the same ingredient — "1 small
  // avocado, pitted and diced" is 6 words and passed, its translation "1 petit
  // avocat, dénoyauté et coupé en dés" is 8 and did not. Word count alone is a
  // language-biased test; the comma is not.
  //
  // Bias is deliberate: for a grocery list, wrongly keeping a stray line is a
  // minor annoyance the user can delete, while wrongly dropping one means they
  // discover the missing ingredient at the store. False positives beat false
  // negatives here.
  const looksPrepped = t.includes(',') || /\([^)]*\)/.test(t);
  if (/^\d+\s+/.test(t) && words.length >= 7 && !MEASUREMENT_UNIT.test(t) && !looksPrepped) {
    return false;
  }

  return true;
}

// Generation asks twice per row (recipe line, then name) and the grocery screen
// asks again for a stored « Autre » row on every render (#772), so remember
// answers. Bounded: cleared wholesale when full.
const CATEGORY_CACHE = new Map<string, GroceryCategory>();
const CATEGORY_CACHE_MAX = 2000;

/**
 * Categorize an ingredient: the bilingual vocabulary first, so "oignon" and
 * "onion" share a rayon (#772), then the English keyword table for a food the
 * vocabulary does not know.
 */
export function categorizeIngredient(ingredientName: string): GroceryCategory {
  const key = ingredientName || '';
  const cached = CATEGORY_CACHE.get(key);
  if (cached) return cached;

  // A loose vocabulary match ("hauts de cuisses de poulet désossés et sans la
  // peau") still beats « Autre », but not an English keyword that names the
  // product ("chocolate cake").
  const vocabulary = categoryFromVocabulary(key);
  let category = vocabulary?.confident ? vocabulary.category : categoryFromKeywords(key);
  if (category === GroceryCategory.Other && vocabulary) category = vocabulary.category;
  if (CATEGORY_CACHE.size >= CATEGORY_CACHE_MAX) CATEGORY_CACHE.clear();
  CATEGORY_CACHE.set(key, category);
  return category;
}

/**
 * A generated row's category, read from the recipe line it came from when there
 * is one. The line says what the cleaned name no longer can: "4 brins de thym"
 * is a fresh herb and « thym » alone a dried one, "1 boîte de tomates" is a can.
 */
function categorizeRow(name: string, sourceLine?: string): GroceryCategory {
  const fromLine = sourceLine ? categorizeIngredient(sourceLine) : GroceryCategory.Other;
  return fromLine !== GroceryCategory.Other ? fromLine : categorizeIngredient(name);
}

function categoryFromKeywords(ingredientName: string): GroceryCategory {
  const normalized = ingredientName.toLowerCase();

  // Check each category for keyword matches
  for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
    if (keywords.some(keyword => normalized.includes(keyword))) {
      return category as GroceryCategory;
    }
  }

  return GroceryCategory.Other;
}

/**
 * Parse quantity from ingredient text
 * Uses the shared ingredient parser which properly anchors regex at the start
 * to avoid extracting wrong numbers from parenthetical content.
 *
 * Examples:
 *   "2 cups milk" → 2
 *   "1/2 teaspoon salt" → 0.5
 *   "0.33 ml (¼ c. à thé) de zeste de lime" → 0.33 (not 540 from parentheses!)
 */
function parseQuantity(text: string): number | undefined {
  return parseAmount(text);
}

/**
 * Clean ingredient name by removing quantity and unit prefix
 * Used for both detailed recipe formats and simple string formats
 */
function cleanIngredientName(ingredientText: string, amount?: number, unit?: string): string {
  let cleaned = ingredientText;

  // Remove leading quantity (numbers, fractions, decimals)
  if (amount !== undefined) {
    cleaned = cleaned.replace(/^[\d\/\.\s]+/, '').trim();
  }

  // Remove unit if found (with optional 's' for plural handling)
  if (unit) {
    const escapedUnit = unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const unitRegex = new RegExp(`^${escapedUnit}s?\\s*`, 'i');
    cleaned = cleaned.replace(unitRegex, '').trim();
  }

  return cleaned;
}

/**
 * Extract a meaningful ingredient name from the original text.
 * For French recipes like "15 g (1/4 tasse) de feuilles de coriandre",
 * extract "feuilles de coriandre" instead of just "feuilles".
 *
 * NOTE: this feeds the CONSOLIDATION key, not the label the user reads. Do not
 * make it more aggressive. It was briefly replaced by `cleanPantryName` (#626)
 * and the extra reduction collapsed distinct foods onto one key: "1/4 teaspoon
 * crushed red pepper" became "red pepper" -> normalizes to "pepper" -> merged
 * with "black pepper" -> the merged row inherited black pepper's staple status
 * and the spice vanished from the list. Grouping wants a conservative name;
 * display wants a tidy one. They are different jobs — see `displayNameFor`.
 */
function extractIngredientNameFromOriginal(original: string): string {
  if (!original) return '';

  let text = original.trim();

  // Step 0: Strip leading bullet markers (- or *) from recipe text
  text = text.replace(/^[-*]\s+/, '');

  // Step 1: Remove leading quantities (numbers, fractions, decimals)
  // Handles: "15", "1/4", "2.5", "½", and compound forms like "5 ¼", "1 1/2"
  text = text.replace(/^[\d½¼¾⅓⅔⅛⅜⅝⅞]+([\/.,][\d½¼¾⅓⅔⅛⅜⅝⅞]+)*(\s+[\d½¼¾⅓⅔⅛⅜⅝⅞]+([\/.,][\d½¼¾⅓⅔⅛⅜⅝⅞]+)*)?\s*/g, '').trim();

  // Step 1.5: Remove "to X" range continuation (e.g., "to ½", "to 4", "to 1/2")
  text = text.replace(/^to\s+[\d½¼¾⅓⅔⅛⅜⅝⅞]+([\/.,][\d½¼¾⅓⅔⅛⅜⅝⅞]+)*\s*/gi, '').trim();

  // Step 1.6: French compound measure phrases, matched as ONE token.
  //
  // MUST run before Step 2. "c. à soupe de farine" is three tokens, and Step 2
  // only knows "c.", so it used to eat the "c.", Step 5 then ate the "à", and
  // the name came out as "soupe de farine" — soup. That head noun is what the
  // staple lookup and the consolidation key are both built from, so flour went
  // back on the grocery list AND occupied a second row (#637).
  const FR_MEASURE_PHRASE =
    /^(?:c\.?\s*(?:à|a)\s*(?:s\.|soupe|th[ée]s?|caf[ée]s?|c\.)|cuill[eè](?:re|rée)s?\s*(?:à|a)\s*(?:soupe|caf[ée]|th[ée]))\s*/i;
  text = text.replace(FR_MEASURE_PHRASE, '').trim();

  // Step 2: Remove multi-character units at the start (require space after).
  // The French counting words are the same kind of unit as "cloves" and
  // "sprigs": without them "4 brins de thym" was keyed « brins thym » and never
  // merged with the thyme of another recipe (#772).
  const multiCharUnits = /^(c\.|tasse|tasses|cup|cups|tbsp|tsp|tablespoon|tablespoons|teaspoon|teaspoons|ml|kg|oz|lb|lbs|pound|pounds|ounce|ounces|gram|grams|kilogram|kilograms|can|cans|clove|cloves|piece|pieces|slice|slices|bunch|bunches|head|heads|stalk|stalks|sprig|sprigs|handfuls?|gousses?|brins?|bottes?|bouquets?|branches?|tiges?|tranches?|poign[ée]es?|pinc[ée]es?)\s+/gi;
  text = text.replace(multiCharUnits, '').trim();

  // Step 3: Remove single-letter units ONLY if followed by space
  text = text.replace(/^[gl]\s+/gi, '').trim();

  // Step 4: Remove parenthetical quantity/unit that may now be at start
  text = text.replace(/^\([^)]*\)\s*/g, '').trim();

  // Step 5: Remove French prepositions at start.
  // The typographic apostrophe is included because scraped French recipes use
  // it more often than the straight one — without it, "45 ml (3 c. à soupe)
  // d’huile de pépins de raisins" kept a name beginning "d’huile" (#637).
  text = text.replace(/^(de\s+|d['’]|du\s+|des\s+|à\s+)/i, '').trim();

  // Step 6: Remove trailing preparation notes in parentheses
  text = text.replace(/\s*\([^)]*\)\s*$/, '').trim();

  // Step 7: Remove trailing commas and preparation notes
  text = text.replace(/,\s*[^,]+$/, '').trim();

  // Step 8: If result is empty, too short, or just a number, return original
  if (!text || text.length < 2 || /^\d+$/.test(text)) {
    return original;
  }

  return text;
}

/**
 * The label the user actually reads on the grocery row.
 *
 * Applied at item-construction time only, deliberately downstream of
 * consolidation, so tidying a name can never merge two foods or flip a row's
 * staple status. `cleanPantryName` is the pantry's reducer and is scored by
 * `npm run eval:pantry-names`; reusing it here is what stops the list showing
 * "medium yellow" and "Large pinch of" (#626).
 *
 * On `usable: false` we keep the consolidation name rather than dropping the
 * row. An ingredient the user has to buy must never disappear, however ugly its
 * label.
 */
function displayNameFor(name: string, original?: string): string {
  const source = (original || '').trim() || name;
  const cleaned = cleanPantryName(source);
  if (cleaned.usable && cleaned.name) return cleaned.name;
  return name;
}

/**
 * Parse unit from ingredient text
 * Uses the shared ingredient parser which properly extracts units
 * from the beginning of the text (after the quantity).
 *
 * Examples:
 *   "2 cups milk" → "cups"
 *   "0.33 ml (¼ c. à thé) de zeste de lime" → "ml"
 *   "0.33 boîte (540 ml/19 oz) de haricots noirs" → "boîte"
 */
function parseUnit(text: string): string | undefined {
  return parseUnitFromText(text);
}

/**
 * Aggregate all ingredients from planned meals
 * Returns ingredients with recipe metadata attached
 */
/**
 * A line worth a grocery row at all. Tap water is a real ingredient — the
 * recipe screen shows it, covered — but nobody buys it, and until #796 « 500 ml
 * d'eau » was listed under « à acheter ».
 */
function isGroceryLine(text: string): boolean {
  return isLikelyIngredient(text) && !isTapWater(text);
}

/**
 * The staple decision for a consolidated row: the shared one, asked about the
 * recipe line the row came from — the same line, and the same unit (read off
 * the line), the recipe screen asks about.
 */
function stapleKeyOfIngredient(ingredient: { original?: string; name?: string }): string | null {
  return stapleKeyForLine(sourceLineOf(ingredient));
}

/**
 * The recipe's own line. After consolidation `original` is a formatted echo
 * ("1 Au goût", "1 huile d'olive") that no longer says what the cook read.
 */
function sourceLineOf(ingredient: { original?: string; name?: string }): string {
  return (ingredient as { _sourceOriginal?: string })._sourceOriginal || ingredient.original || ingredient.name || '';
}

export function aggregateIngredientsFromRecipes(
  meals: PlannedMeal[]
): Array<RecipeIngredient & { _recipeId: string | number; _recipeName: string }> {
  const allIngredients: Array<RecipeIngredient & { _recipeId: string | number; _recipeName: string }> = [];

  console.log(`[GroceryList] Aggregating ingredients from ${meals.length} meals`);

  meals.forEach(meal => {
    if (meal.status === 'cooked') {
      return;
    }

    if (!meal.recipe) {
      console.log(`[GroceryList] Skipping meal - no recipe data`);
      return;
    }

    const recipe = meal.recipe;
    const recipeServings = Number(recipe.servings) || 1;
    const servingsMultiplier = (Number(meal.servings) || recipeServings) / recipeServings;

    console.log(`[GroceryList] Processing recipe: ${recipe.title}`);
    console.log(`[GroceryList]   - Has extendedIngredients: ${!!recipe.extendedIngredients} (${recipe.extendedIngredients?.length || 0})`);
    console.log(`[GroceryList]   - Has ingredients: ${!!recipe.ingredients} (${recipe.ingredients?.length || 0})`);

    // Handle detailed ingredient format (extendedIngredients)
    if (recipe.extendedIngredients && recipe.extendedIngredients.length > 0) {
      const filteredExtended = recipe.extendedIngredients.filter(i => isGroceryLine(i.original || i.name));
      const skippedCount = recipe.extendedIngredients.length - filteredExtended.length;
      if (skippedCount > 0) {
        console.log(`[GroceryList] Filtered out ${skippedCount} non-ingredient items from "${recipe.title}"`);
      }
      filteredExtended.forEach((ingredient, idx) => {
        // Always prefer parsing from original text when available.
        // This ensures consistency with the recipe detail display (which also
        // parses from original) and handles cases where structured amount/unit
        // are wrong (e.g., Spoonacular US-normalized values for metric recipes).
        let baseAmount = ingredient.amount;
        let baseUnit = ingredient.unit || '';

        if (ingredient.original) {
          const parsedAmount = parseQuantity(ingredient.original);
          const parsedUnit = parseUnit(ingredient.original);
          if (parsedAmount && parsedAmount > 0) {
            console.log(`[GroceryList] Parsed from original "${ingredient.original?.substring(0, 50)}" → amount=${parsedAmount}, unit=${parsedUnit || '(none)'} (was: amount=${baseAmount}, unit=${baseUnit || '(none)'})`);
            baseAmount = parsedAmount;
            // When we successfully parse from original text, always use the parsed unit
            // (even if empty/undefined). This prevents Spoonacular's qualifier words like
            // "large" or "whole" from being kept as units, which would block same-ingredient
            // quantities from aggregating (e.g., "4 large eggs" unit="large" vs "6 eggs" unit="").
            baseUnit = parsedUnit ?? '';
          } else if (parsedUnit) {
            baseUnit = parsedUnit;
          }
        }

        // Adjust quantity based on servings (only default to 1 when truly missing)
        const adjustedAmount = baseAmount && Number.isFinite(baseAmount)
          ? baseAmount * servingsMultiplier
          : servingsMultiplier;

        // Extract the full ingredient name from the original text
        // This preserves compound names like "feuilles de coriandre" instead of just "feuilles"
        let ingredientName: string;
        if (ingredient.original) {
          ingredientName = extractIngredientNameFromOriginal(ingredient.original);
        } else {
          // Fallback to cleaning the name field
          const unitInName = parseUnit(ingredient.name);
          const unitToUse = unitInName || baseUnit;
          ingredientName = cleanIngredientName(ingredient.name, baseAmount, unitToUse);
        }

        // Debug: log first 3 ingredients per recipe
        if (idx < 3) {
          console.log(`[GroceryList] Ingredient #${idx}: name="${ingredientName}", amount=${adjustedAmount}, unit="${baseUnit}", original="${ingredient.original?.substring(0, 60)}"`);
        }

        allIngredients.push({
          ...ingredient,
          name: ingredientName, // Use full extracted name
          amount: adjustedAmount,
          unit: baseUnit,
          original: ingredient.original,
          // Track which recipe this ingredient comes from
          _recipeId: recipe.id,
          _recipeName: recipe.title,
        });
      });
    }
    // Handle imported/personal recipe format (simple string array)
    else if (recipe.ingredients && recipe.ingredients.length > 0) {
      const filteredIngredients = recipe.ingredients.filter(isGroceryLine);
      const skippedCount = recipe.ingredients.length - filteredIngredients.length;
      if (skippedCount > 0) {
        console.log(`[GroceryList] Filtered out ${skippedCount} non-ingredient items from "${recipe.title}"`);
      }
      filteredIngredients.forEach((ingredientName, index) => {
        // Convert string to RecipeIngredient format
        const amount = parseQuantity(ingredientName);
        const unit = parseUnit(ingredientName);

        // Extract full ingredient name from the original text
        // This preserves compound names like "feuilles de coriandre"
        const extractedName = extractIngredientNameFromOriginal(ingredientName);

        allIngredients.push({
          id: index,
          name: extractedName,
          original: ingredientName,
          amount: amount && Number.isFinite(amount * servingsMultiplier) ? amount * servingsMultiplier : 1,
          unit: unit || '',
          // Track which recipe this ingredient comes from
          _recipeId: recipe.id,
          _recipeName: recipe.title,
        });
      });
    }
  });

  console.log(`[GroceryList] Total ingredients aggregated: ${allIngredients.length}`);
  return allIngredients;
}

/**
 * Deduplicate ingredients and merge quantities
 */
export function deduplicateIngredients(
  ingredients: Array<RecipeIngredient & { _recipeId: string | number; _recipeName: string }>
): Map<string, RecipeIngredient & { recipeIds: (string | number)[]; recipeNames: string[] }> {
  const deduped = new Map<string, RecipeIngredient & { recipeIds: (string | number)[]; recipeNames: string[] }>();

  ingredients.forEach(ingredient => {
    const normalizedName = normalizeIngredientName(ingredient.name);

    if (deduped.has(normalizedName)) {
      const existing = deduped.get(normalizedName)!;

      // UPDATED: Merge quantities with unit conversion support
      if (existing.unit === ingredient.unit) {
        // Exact unit match - simple addition
        existing.amount += ingredient.amount;
        existing.original = `${existing.amount.toFixed(1)} ${existing.unit} ${ingredient.name}`;
      } else if (areUnitsCompatible(existing.unit, ingredient.unit)) {
        // Units are compatible - convert and merge
        const convertedAmount = convertBetweenUnits(
          ingredient.amount,
          ingredient.unit,
          existing.unit
        );

        if (convertedAmount !== null) {
          existing.amount += convertedAmount;
          existing.original = `${existing.amount.toFixed(1)} ${existing.unit} ${ingredient.name}`;
          console.log(`[UnitConversion] Merged ${ingredient.amount} ${ingredient.unit} → ${convertedAmount.toFixed(1)} ${existing.unit}`);
        } else {
          // Conversion failed - keep separate
          console.log(`[UnitConversion] Failed to convert ${ingredient.unit} to ${existing.unit}`);
        }
      }

      // Track which recipes need this ingredient
      if (!existing.recipeIds.includes(ingredient._recipeId)) {
        existing.recipeIds.push(ingredient._recipeId);
        existing.recipeNames.push(ingredient._recipeName);
      }
    } else {
      // First occurrence
      deduped.set(normalizedName, {
        ...ingredient,
        recipeIds: [ingredient._recipeId],
        recipeNames: [ingredient._recipeName],
      });
    }
  });

  return deduped;
}

/**
 * Smart consolidation of ingredients using normalized name grouping
 * Preserves the full extracted ingredient names and quantities
 * Groups by normalized name to deduplicate "tomatoes" with "tomato"
 */
export function consolidateIngredientsSmart(
  ingredients: Array<RecipeIngredient & { _recipeId: string | number; _recipeName: string }>
): Map<string, RecipeIngredient & { recipeIds: (string | number)[]; recipeNames: string[]; recipeBreakdown: Array<{ recipeId: string; recipeName: string; amount: number; unit: string }> }> {
  console.log(`\n[SmartConsolidation] === STARTING SMART CONSOLIDATION ===`);
  console.log(`[SmartConsolidation] Total ingredients to consolidate: ${ingredients.length}`);

  const consolidated = new Map<string, RecipeIngredient & { recipeIds: (string | number)[]; recipeNames: string[]; recipeBreakdown: Array<{ recipeId: string; recipeName: string; amount: number; unit: string }> }>();

  ingredients.forEach(ingredient => {
    // Use the already-extracted name from aggregateIngredientsFromRecipes
    const displayName = ingredient.name;
    const normalizedKey = normalizeIngredientName(displayName);

    console.log(`[SmartConsolidation] Processing: "${displayName}" → key: "${normalizedKey}"`);
    console.log(`[SmartConsolidation]   - Amount: ${ingredient.amount} ${ingredient.unit || '(no unit)'}`);

    if (consolidated.has(normalizedKey)) {
      const existing = consolidated.get(normalizedKey)!;

      // Merge quantities with unit conversion support
      if (existing.unit === ingredient.unit || (!existing.unit && !ingredient.unit)) {
        // Same unit or both unitless - simple addition
        existing.amount += ingredient.amount;
        console.log(`[SmartConsolidation]   - Merged: new total = ${existing.amount} ${existing.unit || ''}`);
      } else if (areUnitsCompatible(existing.unit, ingredient.unit)) {
        // Units are compatible - convert and merge
        const convertedAmount = convertBetweenUnits(
          ingredient.amount,
          ingredient.unit,
          existing.unit
        );
        if (convertedAmount !== null) {
          existing.amount += convertedAmount;
          console.log(`[SmartConsolidation]   - Converted ${ingredient.amount} ${ingredient.unit} → ${convertedAmount.toFixed(1)} ${existing.unit}`);
        }
      }
      // If units incompatible, keep the existing amount (first wins)

      // Track which recipes need this ingredient
      if (!existing.recipeIds.includes(ingredient._recipeId)) {
        existing.recipeIds.push(ingredient._recipeId);
        existing.recipeNames.push(ingredient._recipeName);
      }

      // Update per-recipe breakdown
      const existingBreakdown = existing.recipeBreakdown.find(
        b => b.recipeId === String(ingredient._recipeId)
      );
      if (existingBreakdown) {
        // Same recipe contributed again (e.g., garlic listed twice) — add to existing
        existingBreakdown.amount += ingredient.amount;
      } else {
        existing.recipeBreakdown.push({
          recipeId: String(ingredient._recipeId),
          recipeName: ingredient._recipeName,
          amount: ingredient.amount,
          unit: ingredient.unit || '',
        });
      }

      // Update the original text to reflect merged quantity
      existing.original = formatIngredientDisplay(existing.amount, existing.unit, existing.name);
    } else {
      // First occurrence - use the full extracted name and actual quantity/unit
      consolidated.set(normalizedKey, {
        id: ingredient.id,
        name: displayName,
        original: formatIngredientDisplay(ingredient.amount, ingredient.unit, displayName),
        // The real recipe line, kept because `original` above is overwritten
        // with a formatted "2 cans white beans" built from the already-reduced
        // name. `displayNameFor` needs the untouched text to do a good job:
        // given the full "2 (15-ounce) cans white beans, such as cannellini or
        // navy" it returns "white beans", but given the formatted echo it can
        // only return "cans white beans".
        _sourceOriginal: ingredient.original,
        amount: ingredient.amount,
        unit: ingredient.unit || '',
        recipeIds: [ingredient._recipeId],
        recipeNames: [ingredient._recipeName],
        recipeBreakdown: [{
          recipeId: String(ingredient._recipeId),
          recipeName: ingredient._recipeName,
          amount: ingredient.amount,
          unit: ingredient.unit || '',
        }],
      } as any);
    }
  });

  console.log(`[SmartConsolidation] Created ${consolidated.size} consolidated groups`);
  console.log(`[SmartConsolidation] === CONSOLIDATION COMPLETE ===\n`);
  return consolidated;
}

/**
 * Format ingredient for display: "2 cups flour" or "3 tomatoes"
 */
function formatIngredientDisplay(amount: number, unit: string | undefined, name: string): string {
  const displayQty = formatQuantity(amount, unit);

  if (unit && unit.trim()) {
    return `${displayQty} ${unit.trim()} ${name}`;
  } else {
    return `${displayQty} ${name}`;
  }
}

/**
 * Get quantity from inventory item (Phase 3 - uses explicit quantity field)
 * Falls back to parsing name for backwards compatibility
 * Examples: quantity=3 → 3, "3 cucumbers" → 3, "cucumber" → 1
 */
function getInventoryQuantity(inventoryItem: InventoryItem): number {
  // Phase 3: Use explicit quantity field if available
  if (inventoryItem.quantity && inventoryItem.quantity > 0) {
    return inventoryItem.quantity;
  }

  // Fallback: Parse from name (backwards compatibility)
  const match = inventoryItem.name.trim().match(/^(\d+(?:\.\d+)?)\s+/);
  if (match) {
    return parseFloat(match[1]);
  }

  // Default to 1
  return 1;
}

/**
 * Calculate required quantities with smart inventory subtraction
 * Returns ingredients with quantity awareness (totalNeeded, quantityInInventory, stillNeed)
 */
export function calculateRequiredQuantities(
  dedupedIngredients: Map<string, RecipeIngredient & { recipeIds: (string | number)[]; recipeNames: string[] }>,
  inventory: InventoryItem[],
  excludedStaples: string[] = []
): {
  needed: Array<RecipeIngredient & { recipeIds: (string | number)[]; recipeNames: string[]; quantityInInventory: number; totalNeeded: number; stillNeed: number }>;
  alreadyHave: Array<RecipeIngredient & { recipeIds: (string | number)[]; recipeNames: string[]; quantityInInventory: number; totalNeeded: number; stillNeed: number; source: string }>;
} {
  // A dish the user cooked is not an ingredient (#663) and must never cancel a
  // shopping line. Keyed on `source`, not the Leftovers category: shop-bought
  // prepared meals land in that category too and are real products.
  const activeInventory = inventory.filter(
    item => (!item.status || item.status === 'active') && !isCookedLeftover(item)
  );

  console.log(`\n[GroceryList] === CALCULATING REQUIRED QUANTITIES ===`);
  console.log(`[GroceryList] Total active inventory items: ${activeInventory.length}`);
  console.log(`[GroceryList] Total deduplicated ingredients: ${dedupedIngredients.size}`);

  // Identity comes from the shared resolver, not from a second normalizer.
  //
  // This function used to run `normalizeIngredientName` + a fuzzy score while
  // `ingredientStatusService` ran a different matcher entirely, so the two
  // reached opposite conclusions about the same line: the recipe screen said
  // you HAD "2 tbsp fresh lime juice" and this function put it on the shopping
  // list. Both now read `ingredientCoverageService`.
  const pantryIndex = buildPantryIndex(
    activeInventory as unknown as Parameters<typeof buildPantryIndex>[0]
  );
  const quantityOfItem = new Map<string, number>();
  activeInventory.forEach(item => {
    quantityOfItem.set(item.id, getInventoryQuantity(item));
  });

  const results: Array<RecipeIngredient & { recipeIds: (string | number)[]; recipeNames: string[]; quantityInInventory: number; totalNeeded: number; stillNeed: number }> = [];
  const alreadyHaveResults: Array<RecipeIngredient & { recipeIds: (string | number)[]; recipeNames: string[]; quantityInInventory: number; totalNeeded: number; stillNeed: number; source: string }> = [];

  console.log(`\n[GroceryList] === PROCESSING INGREDIENTS ===`);
  dedupedIngredients.forEach((ingredient, normalizedName) => {
    const totalNeeded = ingredient.amount > 0 ? ingredient.amount : 1;

    // One question, one answer — the same call the recipe screen makes.
    const coverage = coverageForLine(ingredient.original || ingredient.name, pantryIndex);
    const matchedSource = coverage.state === 'buy' ? undefined : coverage.via[0];
    const matchedItem = matchedSource
      ? activeInventory.find(item => item.id === matchedSource.itemId)
      : undefined;
    const inventoryMatch = matchedItem
      ? { item: matchedItem, quantity: quantityOfItem.get(matchedItem.id) ?? 1 }
      : undefined;
    const matchType = coverage.state === 'probably' ? 'probably' : coverage.via[0]?.relation ?? 'none';

    // A `probably` match is a maybe, and a shopping list is the wrong place to
    // gamble: the row stays on the list until the user confirms the pantry
    // covers it. The recipe screen is where that question gets asked, because
    // that is where the user can see what the recipe actually needs.
    const quantityInInventory =
      inventoryMatch && coverage.state !== 'probably' ? inventoryMatch.quantity : 0;

    console.log(`\n[GroceryList] Processing: "${ingredient.name}" (normalized: "${normalizedName}")`);
    console.log(`[GroceryList]   - Original text: "${ingredient.original}"`);
    console.log(`[GroceryList]   - Total needed: ${totalNeeded} ${ingredient.unit || '(no unit)'}`);
    console.log(`[GroceryList]   - Found in inventory: ${inventoryMatch ? `YES (${matchType} match)` : 'NO'}`);

    if (inventoryMatch) {
      console.log(`[GroceryList]   - Inventory item: "${inventoryMatch.item.name}"`);
      console.log(`[GroceryList]   - Quantity in inventory: ${quantityInInventory}`);
    }

    // Phase 3: Check for unit mismatch
    // Mismatch occurs when inventory has quantity (count-based) but recipe uses weight/volume
    const hasInventory = quantityInInventory > 0;
    const recipeIsCountBased = !ingredient.unit || isCountUnit(ingredient.unit);
    const unitMismatch = hasInventory && !recipeIsCountBased;

    console.log(`[GroceryList]   - Recipe unit: "${ingredient.unit || '(none)'}", isCountBased: ${recipeIsCountBased}`);
    console.log(`[GroceryList]   - Has inventory: ${hasInventory}`);
    console.log(`[GroceryList]   - Unit mismatch: ${unitMismatch}`);

    // Calculate stillNeed: only subtract if units are compatible
    let stillNeed: number;
    if (hasInventory && recipeIsCountBased) {
      // Compatible units - can subtract
      stillNeed = Math.max(0, totalNeeded - quantityInInventory);
      console.log(`[GroceryList]   - Logic: COMPATIBLE UNITS - subtracting inventory`);
      console.log(`[GroceryList]   - Calculation: max(0, ${totalNeeded} - ${quantityInInventory}) = ${stillNeed}`);
    } else if (unitMismatch) {
      // User has the item but units are incompatible — trust they have it
      stillNeed = 0;
      console.log(`[GroceryList]   - Logic: UNIT MISMATCH - user has item, moving to Already Have`);
    } else {
      // No inventory at all
      stillNeed = totalNeeded;
      console.log(`[GroceryList]   - Logic: NO INVENTORY - using full amount`);
      console.log(`[GroceryList]   - Still need: ${stillNeed}`);
    }

    // Fixed staples list (sel, poivre, huile, épices courantes): assumed in the
    // pantry unless explicitly excluded, or overridden by a real pantry entry.
    // If there's an active inventory entry, pantry wins (source: 'inventory').
    // Otherwise, treat as staple (source: 'staple') unless excluded.
    // The recipe screen asks the same function about the same line (#796), so
    // a row this list calls a staple is never « à acheter » over there.
    const stapleKey = stapleKeyOfIngredient(ingredient);
    // Excluded when the cook turned off ANY group the line needs: « Poivre et
    // sel » with salt off has to be bought, as on the recipe screen.
    const isExcluded = stapleKey
      ? stapleKeysForLine(sourceLineOf(ingredient)).some(key =>
          excludedStaples.includes(key)
        )
      : false;
    const hasActivePantryEntry = quantityInInventory > 0;

    if (stapleKey && !isExcluded && !hasActivePantryEntry) {
      // This is an assumed staple, not excluded, and no pantry entry overrides it
      console.log(`[GroceryList]   - ⏭️  STAPLE (${stapleKey}) - counted as already have (${ingredient.name})`);
      alreadyHaveResults.push({
        ...ingredient,
        quantityInInventory: 0,
        totalNeeded,
        stillNeed: 0,
        unitMismatch: false,
        source: 'staple' as const,
        stapleKey,
      } as any);
    } else if (stapleKey && isExcluded && !hasActivePantryEntry) {
      // Excluded staple with nothing in the pantry → goes on the grocery list.
      // The pantry check is what makes the exclusion self-clearing: once the
      // item is actually shelved (bought and checked off, receipt scan, delivery
      // import, manual add) this branch stops firing and the row falls through
      // to the normal inventory path. Without it, an exclusion is permanent and
      // the user re-buys olive oil every single week.
      console.log(`[GroceryList]   - 📍 EXCLUDED STAPLE (${stapleKey}) - on grocery list`);
      results.push({
        ...ingredient,
        quantityInInventory: 0,
        totalNeeded,
        stillNeed: totalNeeded,
        unitMismatch: false,
      } as any);
    } else if (stillNeed > 0) {
      console.log(`[GroceryList]   - ✅ ADDED TO GROCERY LIST (stillNeed: ${stillNeed})`);
      results.push({
        ...ingredient,
        quantityInInventory,
        totalNeeded,
        stillNeed,
        unitMismatch,
      } as any); // Type assertion needed due to complex intersection types
    } else {
      console.log(`[GroceryList]   - ✅ ALREADY HAVE - moved to alreadyHave list${unitMismatch ? ' (unit mismatch)' : ''}`);
      alreadyHaveResults.push({
        ...ingredient,
        quantityInInventory,
        totalNeeded,
        stillNeed: 0,
        unitMismatch,
        source: 'inventory' as const,
      } as any);
    }
  });

  console.log(`\n[GroceryList] === SUMMARY ===`);
  console.log(`[GroceryList] Total items added to grocery list: ${results.length}`);
  console.log(`[GroceryList] Total items already have: ${alreadyHaveResults.length}`);
  console.log(`[GroceryList] Items with unit mismatch: ${results.filter(r => (r as any).unitMismatch).length}`);

  return { needed: results, alreadyHave: alreadyHaveResults };
}

/**
 * Filter out ingredients that are already in inventory (DEPRECATED - use calculateRequiredQuantities)
 * Kept for backward compatibility
 */
export function filterMissingIngredients(
  allIngredients: Array<RecipeIngredient & { _recipeId: string | number; _recipeName: string }>,
  inventory: InventoryItem[]
): Array<RecipeIngredient & { _recipeId: string | number; _recipeName: string }> {
  const activeInventory = inventory.filter(item => item.status === 'active');
  const inventoryNames = new Set(
    activeInventory.map(item => normalizeIngredientName(item.name))
  );

  return allIngredients.filter(ingredient => {
    const normalizedName = normalizeIngredientName(ingredient.name);
    return !inventoryNames.has(normalizedName);
  });
}

/**
 * Calculate waste reduction metrics
 */
export function calculateWasteReduction(
  meals: PlannedMeal[],
  inventory: InventoryItem[]
): WasteReductionMetrics {
  // Get all ingredients needed
  const allIngredients = aggregateIngredientsFromRecipes(meals);
  const dedupedIngredients = deduplicateIngredients(allIngredients);

  const totalIngredients = dedupedIngredients.size;

  // Count how many we already have
  const activeInventory = inventory.filter(item => item.status === 'active');
  const inventoryNames = new Set(
    activeInventory.map(item => normalizeIngredientName(item.name))
  );

  let alreadyHave = 0;
  let expiringIngredientsUsed = 0;
  const skippedItems: SkippedIngredient[] = [];

  dedupedIngredients.forEach((ingredient, normalizedName) => {
    if (inventoryNames.has(normalizedName)) {
      alreadyHave++;

      // Check if this item is expiring soon
      const inventoryItem = activeInventory.find(
        item => normalizeIngredientName(item.name) === normalizedName
      );

      if (inventoryItem?.isExpiringSoon) {
        expiringIngredientsUsed++;
      }

      // Track this as a skipped item
      const quantity = inventoryItem?.quantity || 1;
      const unit = inventoryItem?.unit;
      const isExpiring = inventoryItem?.isExpiringSoon || false;

      skippedItems.push({
        name: ingredient.name,
        quantityInInventory: quantity,
        unit: unit,
        category: categorizeIngredient(ingredient.name),
        recipeNames: ingredient.recipeNames,
        isExpiring,
      });
    } else if (stapleKeyOfIngredient(ingredient)) {
      // Staples (sel, poivre, huile, épices) count as already-have: they are
      // part of the "on a retiré N articles" metric even without an inventory entry
      alreadyHave++;
      skippedItems.push({
        name: ingredient.name,
        quantityInInventory: 1,
        unit: undefined,
        category: categorizeIngredient(ingredient.name),
        recipeNames: ingredient.recipeNames,
        isExpiring: false,
      });
    }
  });

  const needToBuy = totalIngredients - alreadyHave;
  const percentageSaved = totalIngredients > 0 ? (alreadyHave / totalIngredients) * 100 : 0;

  // Estimate savings: $3 per ingredient on average
  const estimatedSavings = alreadyHave * 3;

  return {
    totalIngredients,
    alreadyHave,
    needToBuy,
    percentageSaved,
    estimatedSavings,
    expiringIngredientsUsed,
    skippedItems,
  };
}

/**
 * Generate grocery list from planned meals
 */
/**
 * Recipe ids whose ingredient text is NOT yet in `targetLanguage`.
 *
 * Keyed on `originalLanguage`, NOT on `displayLanguageOf`. That distinction is
 * the bug: ingredient text stays in the language the recipe was WRITTEN in
 * until a full translation is applied, whereas `displayLanguageOf` reports the
 * language the recipe reads as — and the title-only fallback sets
 * `isTranslated` while leaving every ingredient untouched. Asking the display
 * language therefore says "already French" about the exact state that renders
 * English rows (#626).
 *
 * `ingredientsTranslated` is the only thing that clears it, and
 * `applyStoredTranslation` is the only thing that sets that.
 *
 * Exported for the tests, which assert the derivation rather than poking at
 * generateGroceryList's internals.
 */
export function pendingTranslationRecipeIds(
  meals: PlannedMeal[],
  targetLanguage: 'en' | 'fr',
): Set<string> {
  const pending = new Set<string>();
  meals.forEach(meal => {
    const recipe = meal.recipe;
    if (!recipe) return;
    if (recipe.ingredientsTranslated === true) return;

    const writtenIn = recipe.originalLanguage;
    // 'unknown' or absent is not pending: we have no basis to claim the text is
    // wrong, and masking a name we can never improve would hide it forever.
    if (writtenIn !== 'en' && writtenIn !== 'fr') return;
    if (writtenIn === targetLanguage) return;

    pending.add(String(recipe.id));
    pending.add(String(meal.recipeId));
  });
  return pending;
}

/**
 * @param targetLanguage when supplied, items sourced from a recipe whose
 *   ingredients are not yet translated into it are flagged `pendingTranslation`
 *   so the UI can mask the name instead of showing the wrong language. Omitted
 *   by callers that only want metrics, and by the older tests.
 */
export function generateGroceryList(
  meals: PlannedMeal[],
  inventory: InventoryItem[],
  targetLanguage?: 'en' | 'fr',
  excludedStaples: string[] = []
): { items: GroceryItem[]; alreadyHaveItems: GroceryItem[]; metrics: WasteReductionMetrics } {
  // Step 1: Aggregate all ingredients
  const allIngredients = aggregateIngredientsFromRecipes(meals);

  const pendingIds = targetLanguage
    ? pendingTranslationRecipeIds(meals, targetLanguage)
    : new Set<string>();
  // "Any contributing recipe" on purpose: an item consolidated from one
  // translated and one untranslated recipe still has a name that may change,
  // so it stays masked until every contributor has resolved.
  const isPending = (recipeIds: Array<string | number>) =>
    pendingIds.size > 0 && recipeIds.some(id => pendingIds.has(String(id)));

  // Step 2: Smart consolidation (groups by core + cut + modifier)
  const dedupedIngredients = consolidateIngredientsSmart(allIngredients);

  // Step 3: Calculate required quantities with smart inventory subtraction
  const { needed: requiredIngredients, alreadyHave: inventoryHaveIngredients } = calculateRequiredQuantities(dedupedIngredients, inventory, excludedStaples);

  const groceryItems: GroceryItem[] = [];
  const alreadyHaveItems: GroceryItem[] = [];

  // Build grocery items (need to buy)
  requiredIngredients.forEach(ingredient => {
    const stillNeedAmount = Number.isFinite(ingredient.stillNeed) ? ingredient.stillNeed : 0;
    // Consolidation already happened above on the conservative name; this only
    // changes what the row says.
    const cleanName = displayNameFor(
      ingredient.name,
      (ingredient as any)._sourceOriginal || ingredient.original,
    ).replace(/\s+/g, ' ').trim();

    let formattedOriginal: string;
    if (ingredient.unit && ingredient.unit.trim()) {
      const displayQty = stillNeedAmount.toFixed(stillNeedAmount % 1 === 0 ? 0 : 1);
      formattedOriginal = `${displayQty} ${ingredient.unit.trim()} ${cleanName}`;
    } else {
      const displayQty = Math.ceil(stillNeedAmount);
      formattedOriginal = `${displayQty} ${cleanName}`;
    }

    console.log(`[GroceryList] Adding item: "${formattedOriginal}" (stillNeed: ${stillNeedAmount}, inInventory: ${ingredient.quantityInInventory})`);

    groceryItems.push({
      id: `grocery_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: cleanName,
      original: formattedOriginal,
      amount: stillNeedAmount,
      unit: ingredient.unit || undefined,
      category: categorizeRow(cleanName, (ingredient as any)._sourceOriginal || ingredient.original),
      isChecked: false,
      isCustom: false,
      recipeIds: ingredient.recipeIds.map(id => id.toString()),
      recipeNames: ingredient.recipeNames,
      recipeBreakdown: (ingredient as any).recipeBreakdown,
      quantityInInventory: ingredient.quantityInInventory,
      totalNeeded: ingredient.totalNeeded,
      stillNeed: ingredient.stillNeed,
      unitMismatch: (ingredient as any).unitMismatch || false,
      preparationNotes: (ingredient as any)._preparationNotes || [],
      isConsolidated: (ingredient as any)._isConsolidated || false,
      consolidatedCount: (ingredient as any)._consolidatedCount || 1,
      pendingTranslation: isPending(ingredient.recipeIds),
    } as any);
  });

  // Build already-have items (from inventory)
  inventoryHaveIngredients.forEach(ingredient => {
    const cleanName = displayNameFor(
      ingredient.name,
      (ingredient as any)._sourceOriginal || ingredient.original,
    ).replace(/\s+/g, ' ').trim();
    alreadyHaveItems.push({
      id: `have_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: cleanName,
      original: ingredient.original,
      amount: ingredient.amount,
      unit: ingredient.unit || undefined,
      category: categorizeRow(cleanName, (ingredient as any)._sourceOriginal || ingredient.original),
      isChecked: false,
      isCustom: false,
      recipeIds: ingredient.recipeIds.map(id => id.toString()),
      recipeNames: ingredient.recipeNames,
      recipeBreakdown: (ingredient as any).recipeBreakdown,
      quantityInInventory: ingredient.quantityInInventory,
      totalNeeded: ingredient.totalNeeded,
      stillNeed: 0,
      // Carry the classification calculateRequiredQuantities already made.
      // Hardcoding 'inventory' here re-created the #625 bug one layer down:
      // staples were correctly identified, then relabelled as pantry contents
      // on the way to the UI, and stapleKey (which the exclude action needs)
      // was dropped entirely.
      source: (ingredient as any).source ?? 'inventory',
      ...((ingredient as any).stapleKey ? { stapleKey: (ingredient as any).stapleKey } : {}),
    } as any);
  });

  // Step 4: Check manually reviewed meals for confirmed ingredients
  // If user reviewed a meal and did NOT mark an ingredient as missing,
  // that means they confirmed they have it → move from groceryItems to alreadyHaveItems
  meals.forEach(meal => {
    if (!meal.ingredientsReviewed || !meal.recipe) return;

    const recipe = meal.recipe;
    const allMealIngredients = [
      ...(recipe.extendedIngredients || []).map(i => i.name),
      ...(recipe.ingredients || []),
    ];

    // Missing ingredients were explicitly marked by the user
    const missingSet = new Set(meal.missingIngredients.map(i => i.toLowerCase()));

    // Ingredients NOT in the missing list are confirmed by the user
    allMealIngredients.forEach(ingredientName => {
      if (!ingredientName) return;
      const normalized = ingredientName.toLowerCase().trim();

      // Skip if it's in the missing list (user said they need it)
      if (missingSet.has(normalized)) return;

      // Skip if already tracked as already-have (from inventory match)
      const alreadyTracked = alreadyHaveItems.some(
        item => item.name.toLowerCase().includes(normalized) || normalized.includes(item.name.toLowerCase())
      );
      if (alreadyTracked) return;

      // Check if this ingredient is in the grocery list - if so, move it to alreadyHaveItems
      const groceryIndex = groceryItems.findIndex(
        item => item.name.toLowerCase().includes(normalized) || normalized.includes(item.name.toLowerCase())
      );

      if (groceryIndex !== -1) {
        // Move from grocery list to already-have
        const [movedItem] = groceryItems.splice(groceryIndex, 1);
        alreadyHaveItems.push({
          ...movedItem,
          source: 'manual-review',
        } as any);
      } else {
        // Not in grocery list or already-have - add as manual review item
        alreadyHaveItems.push({
          id: `have_manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: ingredientName,
          original: ingredientName,
          category: categorizeIngredient(ingredientName),
          isChecked: false,
          isCustom: false,
          recipeIds: [String(recipe.id)],
          recipeNames: [recipe.title],
          source: 'manual-review',
        } as any);
      }
    });
  });

  // Step 5: Calculate metrics from final alreadyHaveItems
  // Metrics must be derived to ensure banner count === badge count
  const alreadyHave = alreadyHaveItems.length;
  const needToBuy = groceryItems.length;
  const totalIngredients = groceryItems.length + alreadyHaveItems.length;
  const percentageSaved = totalIngredients > 0 ? (alreadyHave / totalIngredients) * 100 : 0;
  const estimatedSavings = alreadyHave * 3;

  const metrics: WasteReductionMetrics = {
    totalIngredients,
    alreadyHave,
    needToBuy,
    percentageSaved,
    estimatedSavings,
    expiringIngredientsUsed: 0, // Deprecated, kept for compatibility
    skippedItems: [], // Deprecated, kept for compatibility
  };

  return { items: groceryItems, alreadyHaveItems, metrics };
}

/**
 * Generate week snapshot hash for change detection.
 *
 * The recipe title is part of the hash, not decoration. Recipe translation runs
 * asynchronously and swaps `meal.recipe` for its translated form *after* the
 * grocery list has been generated and cached. That changes neither the recipe
 * id, the date, the servings nor the status, so without the title the hash
 * matched, the cached list was treated as fresh, and its ingredients stayed in
 * the original language however the app was set. Including the title makes the
 * arrival of a translation — and any later language switch — invalidate the
 * list so it rebuilds from the translated meals.
 *
 * `ingredientsTranslated` is in the hash for the same reason, one step later.
 * The title alone only catches the FIRST translation. When no full translation
 * is stored the meal plan falls back to a title-only one, which writes the
 * translated title onto the meal immediately — so by the time the real
 * ingredient translation arrives the title is already in the target language,
 * the hash matches, and the stale source-language list is served as fresh. That
 * is the whole of #626's second half: the skeleton would never resolve. The two
 * titles come from two separate model calls and are usually byte-identical, so
 * this is not a race that "usually works" — it reliably does not.
 */
export function generateWeekSnapshot(meals: PlannedMeal[]): string {
  const mealIds = meals
    .map(meal =>
      `${meal.recipeId}_${meal.scheduledFor}_${meal.servings}_${meal.status ?? 'planned'}_${meal.recipe?.title ?? ''}_${meal.recipe?.ingredientsTranslated ? 1 : 0}`,
    )
    .sort()
    .join('|');

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < mealIds.length; i++) {
    const char = mealIds.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  return hash.toString(36);
}

/**
 * Format grocery list for copying to clipboard/note-taking apps.
 * Returns plain text grouped by category, excluding checked items and "already have" items.
 *
 * @param items - The grocery items to format (items to buy)
 * @param alreadyHaveItems - Items already in inventory (excluded from output)
 * @param format - 'markdown' prefixes each line with "* " (web); 'plain' uses bare lines (iOS/native)
 * @returns Object with `text` (formatted string) and `count` (number of items)
 */
export function formatGroceryListForCopy(
  items: GroceryItem[],
  alreadyHaveItems?: GroceryItem[],
  format: 'markdown' | 'plain' = 'markdown'
): { text: string; count: number } {
  // Filter out checked items
  const uncheckedItems = items.filter(item => !item.isChecked);

  if (uncheckedItems.length === 0) {
    return { text: '', count: 0 };
  }

  // Group items by category
  const byCategory: Record<GroceryCategory, GroceryItem[]> = {} as Record<GroceryCategory, GroceryItem[]>;

  uncheckedItems.forEach(item => {
    if (!byCategory[item.category]) {
      byCategory[item.category] = [];
    }
    byCategory[item.category].push(item);
  });

  // Get categories with items, sorted by sortOrder
  const categoriesWithItems = Object.entries(byCategory)
    .map(([category, categoryItems]) => ({
      category: category as GroceryCategory,
      items: categoryItems,
      sortOrder: CATEGORY_METADATA[category as GroceryCategory]?.sortOrder ?? 99,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Build output string
  const outputLines: string[] = [];

  categoriesWithItems.forEach(({ category, items: categoryItems }) => {
    categoryItems.forEach(item => {
      let itemText = '';

      // Add quantity if present
      if (item.amount !== undefined && item.amount !== null && item.amount > 0) {
        const formattedAmount = formatQuantity(item.amount, item.unit);

        if (item.unit) {
          itemText = `${formattedAmount} ${item.unit} ${item.name}`;
        } else {
          itemText = `${formattedAmount} ${item.name}`;
        }
      } else {
        itemText = item.name;
      }

      outputLines.push(format === 'markdown' ? `* ${itemText}` : itemText);
    });
  });

  return { text: outputLines.join('\n'), count: uncheckedItems.length };
}

